/**
 * Kuuraii Currency Converter & Multi-Market Engine
 * - Integrates directly with Shopify Markets localization form
 * - Converts the specific server-rendered market price (including higher international prices)
 * - Converts immediately on 1st visit across all PDPs (single & multi-variant)
 * - Converts Cart Drawer and Cart page prices dynamically
 * - Fetches live exchange rates with 12h localStorage caching & offline fallbacks
 * - Bypasses local domestic gateways for international customers to direct native Shopify checkout
 */
(() => {
  'use strict';

  const STORAGE_KEY_RATES = 'kuuraii_currency_rates_v2';
  const CACHE_TTL_MS = 12 * 60 * 60 * 1000; // 12 hours

  // Fallback baseline exchange rates (Base: INR = 1)
  const FALLBACK_RATES = {
    INR: 1,
    USD: 0.0105,
    GBP: 0.0078,
    EUR: 0.0091,
    AED: 0.0386,
    CAD: 0.0145,
    AUD: 0.0162,
    SGD: 0.0141,
    SAR: 0.0394,
    JPY: 1.58,
    MYR: 0.0465,
    NZD: 0.0176,
    KWD: 0.0032,
    QAR: 0.0382,
    CHF: 0.0094,
    SEK: 0.105,
    NOK: 0.112,
    DKK: 0.068,
    HKD: 0.082,
    ZAR: 0.188,
    THB: 0.355,
    PHP: 0.605,
    IDR: 168.0
  };

  const CURRENCY_CONFIG = {
    INR: { symbol: '₹', locale: 'en-IN', decimals: 0 },
    USD: { symbol: '$', locale: 'en-US', decimals: 0 },
    GBP: { symbol: '£', locale: 'en-GB', decimals: 0 },
    EUR: { symbol: '€', locale: 'de-DE', decimals: 0 },
    AED: { symbol: 'AED ', locale: 'en-AE', decimals: 0 },
    CAD: { symbol: 'CA$', locale: 'en-CA', decimals: 0 },
    AUD: { symbol: 'AU$', locale: 'en-AU', decimals: 0 },
    SGD: { symbol: 'SG$', locale: 'en-SG', decimals: 0 },
    SAR: { symbol: 'SAR ', locale: 'en-SA', decimals: 0 },
    JPY: { symbol: '¥', locale: 'ja-JP', decimals: 0 },
    MYR: { symbol: 'RM ', locale: 'en-MY', decimals: 0 },
    NZD: { symbol: 'NZ$', locale: 'en-NZ', decimals: 0 },
    KWD: { symbol: 'KWD ', locale: 'en-KW', decimals: 0 },
    QAR: { symbol: 'QAR ', locale: 'en-QA', decimals: 0 },
    CHF: { symbol: 'CHF ', locale: 'de-CH', decimals: 0 },
    SEK: { symbol: 'SEK ', locale: 'sv-SE', decimals: 0 },
    NOK: { symbol: 'NOK ', locale: 'nb-NO', decimals: 0 },
    DKK: { symbol: 'DKK ', locale: 'da-DK', decimals: 0 },
    HKD: { symbol: 'HK$', locale: 'en-HK', decimals: 0 },
    ZAR: { symbol: 'ZAR ', locale: 'en-ZA', decimals: 0 },
    THB: { symbol: 'THB ', locale: 'th-TH', decimals: 0 },
    PHP: { symbol: 'PHP ', locale: 'en-PH', decimals: 0 },
    IDR: { symbol: 'IDR ', locale: 'id-ID', decimals: 0 }
  };

  const COUNTRY_TO_CURRENCY = {
    IN: 'INR',
    US: 'USD',
    GB: 'GBP',
    AE: 'AED',
    CA: 'CAD',
    AU: 'AUD',
    SG: 'SGD',
    SA: 'SAR',
    JP: 'JPY',
    MY: 'MYR',
    NZ: 'NZD',
    KW: 'KWD',
    QA: 'QAR',
    CH: 'CHF',
    SE: 'SEK',
    NO: 'NOK',
    DK: 'DKK',
    HK: 'HKD',
    ZA: 'ZAR',
    TH: 'THB',
    PH: 'PHP',
    ID: 'IDR',
    // Eurozone
    AT: 'EUR', BE: 'EUR', CY: 'EUR', EE: 'EUR', FI: 'EUR', FR: 'EUR', DE: 'EUR',
    GR: 'EUR', IE: 'EUR', IT: 'EUR', LV: 'EUR', LT: 'EUR', LU: 'EUR', MT: 'EUR',
    NL: 'EUR', PT: 'EUR', SK: 'EUR', SI: 'EUR', ES: 'EUR'
  };

  const PRICE_SELECTORS = [
    // Direct Kuuraii price markers
    '[data-kuuraii-price]',
    '[data-product-price]',
    '[data-product-compare-price]',
    '[data-sticky-product-price]',
    '[data-collection-current-price]',
    '[data-collection-compare-price]',

    // Collection grids & quick card prices
    '.price-row strong',
    '.price-row s',
    '.pdp-sticky-purchase__price strong',
    '.kuuraii-food-sticky-purchase__price strong',
    '[data-sticky-variant-option] strong',
    '.quick-product p.price span',
    '.quick-product p.price s',
    '.quick-product .price span',
    '.quick-product .price s',
    '.kr-collection-card p.price span',
    '.kr-collection-card p.price s',
    '.kr-collection-card .price span',
    '.kr-collection-card .price s',
    '.card-information .price-item',
    '.card__information .price-item',
    '.card-information .price',
    '.product-grid .price-item',
    '.custom-collection-slider-wrapper .price-item',

    // Cart drawer & Cart page
    '.kr-cart-drawer__total strong',
    '.kr-cart-drawer__total .money',
    '.cart-drawer__footer strong',
    '.totals__total-value',
    '.cart-drawer__total',
    '.cart__subtotal',
    '.cart-item__final-price',
    '.cart-item__original-price',
    '.cart-item__price',
    '.cart-item__price-wrapper span.price',
    '.cart-item__price-wrapper strong.price',
    '.cart-item__discounted-prices span.price',
    '.cart-item__discounted-prices s.price',
    '.cart-item__old-price',
    'span.price.price--end',
    'strong.price.price--end',
    'td.cart-item__details .product-option:not(dl *)',
    'td.cart-item__totals span.price',
    'td.cart-item__totals s.price',

    // Dawn & standard theme price selectors
    '.price-item--regular',
    '.price-item--sale',
    '.price-item--last',
    '.price-item',
    '.variant-item__old-price',
    '.price__regular .price-item',
    '.price__sale .price-item',
    '.price__container .price-item',
    '.price--on-sale .price-item',
    '.unit-price span:first-child',
    '.price-per-item span',
    '.sticky-atc__price',
    '.kuuraii-convert-price',

    // Generic Shopify money wrappers
    'span.money',
    '.money'
  ].join(', ');

  let currentRates = Object.assign({}, FALLBACK_RATES);
  let activeCountry = 'IN';
  let activeCurrency = 'INR';
  let isConverting = false;
  let isSwitchingMarket = false;
  let debounceTimer = null;

  function resolveActiveMarket() {
    // 1. Check window.KuuraiiStore injected by Liquid
    if (window.KuuraiiStore && window.KuuraiiStore.country) {
      return window.KuuraiiStore.country.toUpperCase();
    }
    // 2. Check header or drawer select value
    const sel = document.querySelector('#KuuraiiHeaderCountrySelect, #KuuraiiMobileCountrySelect');
    if (sel && sel.value) {
      return sel.value.toUpperCase();
    }
    // 3. Check Shopify global object
    if (window.Shopify && window.Shopify.country) {
      return window.Shopify.country.toUpperCase();
    }
    return 'IN';
  }

  function getCurrencyForCountry(countryCode) {
    if (!countryCode) return 'INR';
    const code = countryCode.toUpperCase();
    if (code === 'IN') return 'INR';
    return COUNTRY_TO_CURRENCY[code] || 'USD';
  }

  function loadCachedRates() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_RATES);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.timestamp && (Date.now() - parsed.timestamp < CACHE_TTL_MS) && parsed.rates) {
          currentRates = Object.assign({}, FALLBACK_RATES, parsed.rates);
          return true;
        }
      }
    } catch (e) {}
    return false;
  }

  function fetchLiveRates() {
    fetch('https://open.er-api.com/v6/latest/INR')
      .then((res) => {
        if (!res.ok) throw new Error('Network error');
        return res.json();
      })
      .then((data) => {
        if (data && data.result === 'success' && data.rates) {
          const newRates = Object.assign({}, FALLBACK_RATES);
          Object.keys(CURRENCY_CONFIG).forEach((cur) => {
            if (data.rates[cur]) {
              newRates[cur] = data.rates[cur];
            }
          });
          currentRates = newRates;
          try {
            localStorage.setItem(STORAGE_KEY_RATES, JSON.stringify({
              timestamp: Date.now(),
              rates: newRates
            }));
          } catch (e) {}

          if (activeCurrency !== 'INR') {
            convertAll();
          }
        }
      })
      .catch(() => {});
  }

  function formatPrice(amount, currencyCode) {
    const cfg = CURRENCY_CONFIG[currencyCode] || { symbol: currencyCode + ' ', decimals: 0 };
    const rounded = Math.round(amount);
    if (currencyCode === 'INR') {
      return '₹ ' + rounded.toLocaleString('en-IN');
    }
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(rounded);
    } catch (e) {
      return cfg.symbol + rounded;
    }
  }

  function parseInrValue(text) {
    if (!text || typeof text !== 'string') return null;
    const clean = text.replace(/,/g, '').trim();
    const match = clean.match(/(?:Rs\.?|₹)\s*(\d+(?:\.\d+)?)/i) ||
                  clean.match(/(\d+(?:\.\d+)?)\s*(?:INR)/i) ||
                  clean.match(/\b(\d{2,}(?:\.\d+)?)\b/);
    if (match) {
      const val = parseFloat(match[1]);
      if (!isNaN(val) && val > 0) return val;
    }
    return null;
  }

  function convertElement(el) {
    if (!el || el.nodeType !== Node.ELEMENT_NODE) return;
    // The review cart already uses Shopify's market prices and exact currency subunits.
    // Reformatting these as whole rupees would make item sums disagree with checkout.
    if (el.closest('.kr-cart-review')) return;
    if (el.classList && el.classList.contains('visually-hidden')) return;
    if (el.tagName === 'DL' || el.tagName === 'DT' || el.tagName === 'DD') return;

    // Check if element has an inner .money child
    const moneyChild = el.querySelector('.money');
    if (moneyChild && el !== moneyChild && el.children.length === 1) {
      convertElement(moneyChild);
      return;
    }

    // Dynamic variant price binding for PDP & collection cards
    // Ensures current price always matches the active variant rather than being stuck on an old variant's baseInr
    if (el.hasAttribute('data-product-price')) {
      const scope = el.closest('[data-product-root], .kuuraii-proto, .millet-pdp, .vv-pdp, .product') || document;
      const activeBtn = scope.querySelector('[data-variant-button].is-active, [data-variant-button].active');
      if (activeBtn && activeBtn.dataset.baseVariantPrice) {
        el.dataset.baseInr = activeBtn.dataset.baseVariantPrice;
      }
    } else if (el.hasAttribute('data-product-compare-price')) {
      const scope = el.closest('[data-product-root], .kuuraii-proto, .millet-pdp, .vv-pdp, .product') || document;
      const activeBtn = scope.querySelector('[data-variant-button].is-active, [data-variant-button].active');
      if (activeBtn && activeBtn.dataset.baseVariantCompare) {
        el.dataset.baseInr = activeBtn.dataset.baseVariantCompare;
      } else if (activeBtn) {
        delete el.dataset.baseInr;
        el.textContent = '';
        el.hidden = true;
        return;
      }
    } else if (el.hasAttribute('data-sticky-product-price')) {
      const activeBtn = document.querySelector('.kuuraii-proto [data-variant-button].is-active, .kuuraii-proto [data-variant-button].active, [data-variant-button].is-active');
      if (activeBtn && activeBtn.dataset.baseVariantPrice) {
        el.dataset.baseInr = activeBtn.dataset.baseVariantPrice;
      }
    } else if (el.hasAttribute('data-collection-current-price')) {
      const card = el.closest('.quick-product, .kr-collection-card, [data-collection-product-card]');
      const activeBtn = card && card.querySelector('[data-collection-variant].is-selected, [data-collection-variant].active');
      if (activeBtn && activeBtn.dataset.baseVariantPrice) {
        el.dataset.baseInr = activeBtn.dataset.baseVariantPrice;
      }
    } else if (el.hasAttribute('data-collection-compare-price')) {
      const card = el.closest('.quick-product, .kr-collection-card, [data-collection-product-card]');
      const activeBtn = card && card.querySelector('[data-collection-variant].is-selected, [data-collection-variant].active');
      if (activeBtn && activeBtn.dataset.baseVariantCompare) {
        el.dataset.baseInr = activeBtn.dataset.baseVariantCompare;
      } else if (activeBtn) {
        delete el.dataset.baseInr;
        el.textContent = '';
        el.hidden = true;
        return;
      }
    }

    let baseInr = null;
    if (el.dataset.baseInr) {
      baseInr = parseFloat(el.dataset.baseInr);
    } else {
      let textToParse = '';
      if (moneyChild) {
        textToParse = moneyChild.textContent || '';
      } else if (el.children.length > 0) {
        for (let i = 0; i < el.childNodes.length; i++) {
          const node = el.childNodes[i];
          if (node.nodeType === Node.TEXT_NODE && /\d/.test(node.nodeValue)) {
            textToParse += ' ' + node.nodeValue;
          }
        }
      }
      if (!textToParse) {
        textToParse = el.textContent || '';
      }
      baseInr = parseInrValue(textToParse);
      if (baseInr && !isNaN(baseInr)) {
        el.dataset.baseInr = baseInr;
      }
    }

    if (!baseInr || isNaN(baseInr)) return;

    // If activeCurrency is INR, ensure it shows original domestic INR format
    if (activeCurrency === 'INR') {
      const inrFormatted = formatPrice(baseInr, 'INR');
      if (moneyChild) {
        moneyChild.textContent = inrFormatted;
      } else if (el.children.length === 0) {
        el.textContent = inrFormatted;
      }
      return;
    }

    // Convert using live market exchange rate
    const rate = currentRates[activeCurrency] || 1;
    const convertedAmount = baseInr * rate;
    const formatted = formatPrice(convertedAmount, activeCurrency);

    if (moneyChild) {
      moneyChild.textContent = formatted;
      return;
    }

    // If element has NO child elements (most price elements, cart totals, PDP prices)
    if (el.children.length === 0) {
      el.textContent = formatted;
      return;
    }

    // If element has child elements (e.g. tax label or compare strike-through)
    let replaced = false;
    for (let i = 0; i < el.childNodes.length; i++) {
      const node = el.childNodes[i];
      if (node.nodeType === Node.TEXT_NODE && /\d/.test(node.nodeValue)) {
        node.nodeValue = node.nodeValue.replace(/(?:Rs\.?|₹|[^\d\s.,]+)?\s*[\d,]+(?:\.\d+)?(?:\s*INR)?/i, () => formatted);
        replaced = true;
        break;
      }
    }

    if (!replaced && el.children.length === 1 && el.firstElementChild) {
      el.firstElementChild.textContent = formatted;
    }
  }

  function updateVariantButtons() {
    const rate = currentRates[activeCurrency] || 1;

    // 1. PDP variant selector buttons [data-variant-button]
    document.querySelectorAll('[data-variant-button]').forEach((btn) => {
      let basePrice = btn.dataset.baseVariantPrice;
      if (!basePrice) {
        basePrice = parseInrValue(btn.getAttribute('data-variant-price') || '');
        if (basePrice) btn.dataset.baseVariantPrice = basePrice;
      }
      if (basePrice) {
        const converted = activeCurrency === 'INR' ? basePrice : (basePrice * rate);
        btn.setAttribute('data-variant-price', formatPrice(converted, activeCurrency));
      }

      let baseCompare = btn.dataset.baseVariantCompare;
      if (!baseCompare) {
        baseCompare = parseInrValue(btn.getAttribute('data-variant-compare') || '');
        if (baseCompare) btn.dataset.baseVariantCompare = baseCompare;
      }
      if (baseCompare) {
        const convertedCompare = activeCurrency === 'INR' ? baseCompare : (baseCompare * rate);
        btn.setAttribute('data-variant-compare', formatPrice(convertedCompare, activeCurrency));
      }
    });

    // 2. Collection card variant buttons [data-collection-variant]
    document.querySelectorAll('[data-collection-variant]').forEach((btn) => {
      let basePrice = btn.dataset.baseVariantPrice;
      if (!basePrice) {
        basePrice = parseInrValue(btn.getAttribute('data-variant-price') || '');
        if (basePrice) btn.dataset.baseVariantPrice = basePrice;
      }
      if (basePrice) {
        const converted = activeCurrency === 'INR' ? basePrice : (basePrice * rate);
        btn.setAttribute('data-variant-price', formatPrice(converted, activeCurrency));
      }

      let baseCompare = btn.dataset.baseVariantCompare;
      if (!baseCompare) {
        baseCompare = parseInrValue(btn.getAttribute('data-variant-compare-price') || '');
        if (baseCompare) btn.dataset.baseVariantCompare = baseCompare;
      }
      if (baseCompare) {
        const convertedCompare = activeCurrency === 'INR' ? baseCompare : (baseCompare * rate);
        btn.setAttribute('data-variant-compare-price', formatPrice(convertedCompare, activeCurrency));
      }
    });

    // 3. Sticky purchase options [data-sticky-variant-option]
    document.querySelectorAll('[data-sticky-variant-option]').forEach((btn) => {
      let basePrice = btn.dataset.baseVariantPrice;
      if (!basePrice) {
        basePrice = parseInrValue(btn.getAttribute('data-variant-price') || '');
        if (basePrice) btn.dataset.baseVariantPrice = basePrice;
      }
      if (basePrice) {
        const converted = activeCurrency === 'INR' ? basePrice : (basePrice * rate);
        btn.setAttribute('data-variant-price', formatPrice(converted, activeCurrency));
        const strong = btn.querySelector('strong');
        if (strong) strong.textContent = formatPrice(converted, activeCurrency);
      }
    });
  }

  function updateCartNotice() {
    const notice = document.getElementById('KuuraiiCartCurrencyNotice');
    if (notice) {
      notice.style.display = 'none';
      notice.innerHTML = '';
      notice.remove();
    }
  }

  function convertAll() {
    if (isConverting) return;
    isConverting = true;

    try {
      const elements = document.querySelectorAll(PRICE_SELECTORS);
      for (let i = 0; i < elements.length; i++) {
        convertElement(elements[i]);
      }
      updateVariantButtons();
      updateCartNotice();
    } catch (err) {
      console.error('[KuuraiiCurrency] Error during convertAll:', err);
    } finally {
      isConverting = false;
    }
  }

  function scheduleConvert() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      convertAll();
    }, 20);
  }

  function setupEventListeners() {

    // Variant click listener: synchronize dataset.baseInr immediately on price elements
    document.addEventListener('click', (e) => {
      const vBtn = e.target.closest('[data-variant-button], [data-collection-variant], [data-sticky-variant-option]');
      if (vBtn) {
        const basePrice = vBtn.dataset.baseVariantPrice;
        const baseCompare = vBtn.dataset.baseVariantCompare;

        // PDP Price elements
        const root = vBtn.closest('[data-product-root], .kuuraii-proto, .millet-pdp, .vv-pdp, .product');
        if (root) {
          const priceNode = root.querySelector('[data-product-price]');
          if (priceNode && basePrice) priceNode.dataset.baseInr = basePrice;
          const compareNode = root.querySelector('[data-product-compare-price]');
          if (compareNode) {
            if (baseCompare) compareNode.dataset.baseInr = baseCompare;
            else delete compareNode.dataset.baseInr;
          }
        }

        // Collection Card Price elements
        const card = vBtn.closest('.quick-product, .kr-collection-card, [data-collection-product-card]');
        if (card) {
          const currentPrice = card.querySelector('[data-collection-current-price]');
          if (currentPrice && basePrice) currentPrice.dataset.baseInr = basePrice;
          const comparePrice = card.querySelector('[data-collection-compare-price]');
          if (comparePrice) {
            if (baseCompare) comparePrice.dataset.baseInr = baseCompare;
            else delete comparePrice.dataset.baseInr;
          }
        }

        // Sticky PDP price
        const stickyPrice = document.querySelector('[data-sticky-product-price], .pdp-sticky-purchase__price strong');
        if (stickyPrice && basePrice) stickyPrice.dataset.baseInr = basePrice;

        setTimeout(convertAll, 10);
        setTimeout(convertAll, 60);
      }

      // Cart Drawer open triggers: convert prices immediately
      const cartTrigger = e.target.closest('#cart-icon-bubble, .cart-button, [data-cart-toggle], .header__icon--cart, a[href*="/cart"]');
      if (cartTrigger) {
        setTimeout(convertAll, 20);
        setTimeout(convertAll, 150);
        setTimeout(convertAll, 400);
      }
    });

    // Observer for CartDrawer and dynamic DOM injections
    const observer = new MutationObserver((mutations) => {
      if (isConverting) return;
      let shouldConvert = false;
      for (let i = 0; i < mutations.length; i++) {
        const m = mutations[i];
        if (m.type === 'attributes' && (m.attributeName === 'class' || m.attributeName === 'open')) {
          shouldConvert = true;
          break;
        }
        if (m.addedNodes.length > 0) {
          for (let j = 0; j < m.addedNodes.length; j++) {
            const node = m.addedNodes[j];
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.matches && (node.matches(PRICE_SELECTORS) || node.querySelector(PRICE_SELECTORS) || node.matches('cart-drawer, #CartDrawer, .drawer__inner'))) {
                shouldConvert = true;
                break;
              }
            }
          }
        }
        if (shouldConvert) break;
      }
      if (shouldConvert) {
        scheduleConvert();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'open', 'data-variant-price']
    });

    // Specific observer on cart drawer if present
    const cartDrawer = document.querySelector('cart-drawer, #CartDrawer');
    if (cartDrawer) {
      const cdObserver = new MutationObserver(() => {
        scheduleConvert();
      });
      cdObserver.observe(cartDrawer, { childList: true, subtree: true });
    }

    // Theme and cart events
    ['cart:updated', 'cart:refresh', 'cart:rendered', 'product:variant-change'].forEach((ev) => {
      document.addEventListener(ev, scheduleConvert);
      window.addEventListener(ev, scheduleConvert);
    });

    // Intercept fetch requests for cart mutations
    const originalFetch = window.fetch;
    if (originalFetch) {
      window.fetch = function(...args) {
        return originalFetch.apply(this, args).then((response) => {
          try {
            const url = (typeof args[0] === 'string') ? args[0] : (args[0] && args[0].url) || '';
            if (url && (url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/update') || url.includes('/cart.js') || url.includes('sections='))) {
              setTimeout(scheduleConvert, 50);
              setTimeout(scheduleConvert, 200);
              setTimeout(scheduleConvert, 500);
            }
          } catch (err) {}
          return response;
        });
      };
    }
  }

  function init() {
    activeCountry = resolveActiveMarket();
    activeCurrency = getCurrencyForCountry(activeCountry);

    try {
      if (activeCountry && activeCountry !== 'IN') {
        sessionStorage.setItem('quick_currency_code', activeCurrency);
        sessionStorage.setItem('quick_cc_currency', activeCurrency);
        localStorage.setItem('quick_localization', activeCountry);
        localStorage.setItem('quick_country', activeCountry);
      }
    } catch (e) {}

    loadCachedRates();
    // Immediate conversion on 1st visit
    convertAll();
    setupEventListeners();
    fetchLiveRates();

    // Additional immediate passes to ensure zero flicker on 1st visit
    requestAnimationFrame(convertAll);
    setTimeout(convertAll, 100);
    setTimeout(convertAll, 400);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('load', convertAll);

  // Public API
  window.KuuraiiCurrency = {
    getCountry: () => activeCountry,
    getCurrency: () => activeCurrency,
    getRates: () => Object.assign({}, currentRates),
    setCountry: (code) => {
      const sel = document.querySelector('#KuuraiiHeaderCountrySelect, #KuuraiiMobileCountrySelect');
      if (sel) {
        sel.value = code.toUpperCase();
        if (sel.form) sel.form.submit();
      }
    },
    convertAll,
    formatPrice
  };
})();

